/**
 * @file Content.tsx
 * @description Component for displaying and managing generated SEO content.
 * Handles content viewing, editing, regeneration and export functionality.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";

import { auth } from "../../config/firebaseConfig";
import config from "../../config/config";
import log from "../../config/logging";

import {
  GenerateContentFormData,
  ContentDebugInfo,
  RefineContentPayload,
} from "../../types";
import { DownloadFormat } from "../../types/download";

import { useUserDetails } from "../../context/UserDetailsContext";
import { useContentEditing } from "../../hooks/useContentEditing";
import useGenerateContent from "../../hooks/useGenerateContent";

import { downloadContent, refineContent } from "../../services/contentService";
import { publishContent } from "../../services/wordpressService";

import Loading from "../../components/common/Loading";
import GeneratedContent from "../../components/common/GeneratedContent";
import ContentForm from "../../components/common/ContentForm";
import GeneratedContentEditor from "../../components/common/GeneratedContentEditor";
import PencilIcon from "../../components/common/icons/PencilIcon";
import RefineContentModal from "../../components/common/RefineContentModal";

interface ContentApiResponse {
  success: boolean;
  content: {
    id: string;
    title: string; // Unified title: generated if available, otherwise reference
    metaDescription: string;
    summary?: string;
    content: string;
    jsonLd?: string;
    keyword: string;
    website: string;
    contentType?: string;
    clientInfo?: string;
    relatedKeywords: string[];
    peopleAlsoAsk?: string[];
    debugInfo?: ContentDebugInfo;
    // Legacy fields for retrocompatibility
    topic?: string;
    audience?: string;
  };
}

type ContentDataState = {
  title: string;
  metaDescription: string;
  summary?: string;
  content: string;
  jsonLd?: string;
  debugInfo?: ContentDebugInfo;
};

const Content: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [contentData, setContentData] = useState<ContentDataState>({
    title: "",
    metaDescription: "",
    summary: undefined,
    content: "",
    jsonLd: undefined,
    debugInfo: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<GenerateContentFormData>({
    keyword: "",
    website: "",
    contentType: "",
    clientInfo: "",
    relatedKeywords: [],
    peopleAlsoAsk: [],
    title: "",
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isPublishingToWordPress, setIsPublishingToWordPress] = useState(false);

  const { refreshUserDetails } = useUserDetails();

  const {
    isEditing,
    editedData,
    startEditing,
    cancelEditing,
    updateEditedData,
  } = useContentEditing(contentData);

  const {
    generateContent,
    isLoading: isGenerating,
    setIsLoading: setIsGenerating,
  } = useGenerateContent();

  const updateFormData = useCallback(
    (
      field: keyof GenerateContentFormData,
      value:
        | string
        | string[]
        | ((prevValue: string | string[]) => string | string[])
    ) => {
      setFormData((prev) => ({
        ...prev,
        [field]:
          typeof value === "function"
            ? (value as Function)(prev[field])
            : value,
      }));
    },
    []
  );

  const fetchContent = useCallback(async () => {
    if (loading) return;

    if (!user) {
      log.warn("User not authenticated, redirecting to login");
      toast.error("Per favore, accedi per visualizzare questa pagina");
      navigate("/accedi");
      return;
    }

    try {
      setIsLoading(true);
      const token = await user.getIdToken(true);
      const response = await fetch(`${config.backendUrl}/api/contents/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Errore: ${response.statusText}`);
      }

      const data: ContentApiResponse = await response.json();
      log.debug("Content fetched successfully", { data });

      setContentData({
        title: data.content.title,
        metaDescription: data.content.metaDescription,
        summary: data.content.summary,
        content: data.content.content,
        jsonLd: data.content.jsonLd,
        debugInfo: data.content.debugInfo,
      });
      setFormData({
        keyword: data.content.keyword || "",
        website: data.content.website || "",
        // Always show new fields, even if empty (for legacy content, they will be empty)
        contentType: data.content.contentType || "",
        clientInfo: data.content.clientInfo || "",
        relatedKeywords: data.content.relatedKeywords || [],
        peopleAlsoAsk: data.content.peopleAlsoAsk || [],
        // Use unified title as reference title in form (generated || reference)
        // When regenerating, this will be used as the new reference title
        title: data.content.title || "",
      });
    } catch (error: unknown) {
      log.error("Error fetching content", { error });
      toast.error("Errore durante il recupero del contenuto");
    } finally {
      setIsLoading(false);
    }
  }, [id, user, loading, navigate]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleDownload = async (format: DownloadFormat) => {
    if (!id) {
      log.error("Content ID is undefined");
      toast.error("Errore: ID del contenuto non trovato");
      return;
    }

    try {
      setIsDownloading(true);
      await downloadContent(id, contentData.title, format);

      toast.success(
        `Contenuto scaricato in formato ${
          format === "wxr" ? "WordPress XML" : "Word"
        }`
      );

      log.info(`Content downloaded successfully as ${format}`, {
        contentId: id,
        format,
      });
    } catch (error) {
      log.error(`Download failed for format: ${format}`, {
        error,
        contentId: id,
      });

      toast.error(
        `Errore durante il download in formato ${
          format === "wxr" ? "WordPress XML" : "Word"
        }`
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopy = async (htmlContent: string) => {
    try {
      const blob = new Blob([htmlContent], { type: "text/html" });
      const clipboardItem = new ClipboardItem({ "text/html": blob });

      await navigator.clipboard.write([clipboardItem]);
      toast.success("Contenuto copiato negli appunti");
    } catch (err) {
      log.error("Error copying content", err);
      toast.error(
        "Impossibile copiare il contenuto. Prova a farlo manualmente."
      );
    }
  };

  const handleSubmit = async (data: GenerateContentFormData) => {
    if (!id) {
      log.error("Content ID is undefined");
      toast.error("Errore: ID del contenuto non trovato");
      return;
    }

    try {
      setIsGenerating(true);
      log.debug("Content regeneration requested", { id, data });

      await generateContent({ ...data, contentDetailId: id }, async () => {
        log.debug("Content regenerated successfully", { id });
        toast.success("Contenuto rigenerato con successo");
        await fetchContent(); // Refresh the content after regeneration
      });

      await refreshUserDetails();
    } catch (error) {
      log.error("Error regenerating content", error);
      toast.error("Errore durante la rigenerazione del contenuto");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = useCallback(() => {
    setFormData({
      keyword: "",
      website: "",
      contentType: "",
      clientInfo: "",
      relatedKeywords: [],
      peopleAlsoAsk: [],
      title: "",
    });

    if (isEditing) {
      cancelEditing();
    }

    log.debug("Form reset");
  }, [isEditing, cancelEditing]);

  const handleContentChange = useCallback(
    (data: Partial<typeof contentData>) => {
      updateEditedData(data);
    },
    [updateEditedData]
  );

  const handleRefineClick = useCallback(() => {
    setIsRefineModalOpen(true);
  }, []);

  const handleRefineClose = useCallback(() => {
    if (!isRefining) {
      setIsRefineModalOpen(false);
    }
  }, [isRefining]);

  const handleRefineSubmit = useCallback(
    async ({
      instructions,
      includeOriginalInputs,
      includeInternalLinks,
    }: RefineContentPayload) => {
      if (!id) {
        log.error("Content ID is undefined for refinement");
        toast.error("Errore: ID del contenuto non trovato");
        return;
      }

      try {
        setIsRefining(true);
        log.debug("Content refinement requested", {
          contentId: id,
          includeOriginalInputs,
          includeInternalLinks,
        });

        const response = await refineContent(id, {
          instructions,
          includeOriginalInputs,
          includeInternalLinks,
        });

        setContentData((prev) => ({
          ...prev,
          title: response.content.title,
          metaDescription: response.content.metaDescription,
          summary: response.content.summary,
          content: response.content.content,
          jsonLd: response.content.jsonLd,
        }));

        if (isEditing) {
          cancelEditing();
        }

        toast.success("Refinement completato con successo");
        setIsRefineModalOpen(false);
      } catch (error) {
        log.error("Error refining content", error);
        toast.error("Errore durante il refinement del contenuto");
      } finally {
        setIsRefining(false);
      }
    },
    [id, isEditing, cancelEditing]
  );

  const handleSave = useCallback(async () => {
    try {
      const token = await user?.getIdToken(true);
      const response = await fetch(`${config.backendUrl}/api/contents/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editedData),
      });

      if (!response.ok) {
        throw new Error(`Errore: ${response.statusText}`);
      }

      setContentData(editedData);
      cancelEditing();
      toast.success("Contenuto salvato con successo");
    } catch (error) {
      log.error("Error saving content", error);
      toast.error("Errore durante il salvataggio del contenuto");
    }
  }, [user, id, editedData, cancelEditing]);

  const handlePublishToWordPress = useCallback(async () => {
    if (!id) {
      log.error("Content ID is undefined");
      toast.error("Errore: ID del contenuto non trovato");
      return;
    }

    try {
      setIsPublishingToWordPress(true);
      const result = await publishContent(id);
      toast.success(
        `Contenuto pubblicato su WordPress! ${result.link ? `Visualizza: ${result.link}` : ""}`
      );
      log.info("Content published to WordPress successfully", {
        contentId: id,
        postId: result.postId,
      });
    } catch (error) {
      log.error("Error publishing content to WordPress", { error });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Errore durante la pubblicazione su WordPress";
      toast.error(errorMessage);
    } finally {
      setIsPublishingToWordPress(false);
    }
  }, [id]);

  if (loading || isLoading) {
    return <Loading />;
  }

  return (
    <section className="mx-auto max-w-content">
      <h2 className="!text-5xl !font-normal text-primary-content !mb-16 flex items-center gap-6">
        <PencilIcon className="w-11 h-11" />
        Ecco il tuo contenuto!
      </h2>
      <div className="flex flex-col items-start gap-4 sm:flex-row">
        <ContentForm
          {...formData}
          setKeyword={(value) =>
            updateFormData(
              "keyword",
              typeof value === "function" ? value(formData.keyword) : value
            )
          }
          setWebsite={(value) =>
            updateFormData(
              "website",
              typeof value === "function" ? value(formData.website) : value
            )
          }
          setContentType={(value) =>
            updateFormData(
              "contentType",
              typeof value === "function" ? value(formData.contentType || "") : value
            )
          }
          setClientInfo={(value) =>
            updateFormData(
              "clientInfo",
              typeof value === "function" ? value(formData.clientInfo || "") : value
            )
          }
          setRelatedKeywords={(value) =>
            updateFormData(
              "relatedKeywords",
              typeof value === "function"
                ? value(formData.relatedKeywords)
                : value
            )
          }
          peopleAlsoAsk={formData.peopleAlsoAsk}
          setPeopleAlsoAsk={(value) =>
            updateFormData(
              "peopleAlsoAsk",
              typeof value === "function"
                ? value(formData.peopleAlsoAsk || [])
                : value
            )
          }
          title={formData.title}
          setTitle={(value) =>
            updateFormData(
              "title",
              typeof value === "function"
                ? value(formData.title || "")
                : value
            )
          }
          onSubmit={handleSubmit}
          isLoading={isLoading || isGenerating}
          variant="edit"
          onReset={handleReset}
        />
        {isEditing ? (
          <GeneratedContentEditor
            title={editedData.title}
            metaDescription={editedData.metaDescription}
            content={editedData.content}
            onChange={handleContentChange}
            onSave={handleSave}
            onCancel={cancelEditing}
          />
        ) : (
          contentData.content && (
            <GeneratedContent
              contentId={id || ""}
              title={contentData.title}
              metaDescription={contentData.metaDescription}
              summary={contentData.summary}
              content={contentData.content}
              jsonLd={contentData.jsonLd}
              debugInfo={contentData.debugInfo}
              onDownload={handleDownload}
              onCopy={handleCopy}
              onEdit={startEditing}
              onRefine={handleRefineClick}
              onPublishToWordPress={handlePublishToWordPress}
              isPublishingToWordPress={isPublishingToWordPress}
              isLoading={isDownloading}
              isRefining={isRefining}
            />
          )
        )}
      </div>
      <RefineContentModal
        isOpen={isRefineModalOpen}
        isSubmitting={isRefining}
        onSubmit={handleRefineSubmit}
        onClose={handleRefineClose}
      />
    </section>
  );
};

export default Content;
